import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, Siren, ClipboardCheck, ScrollText, ExternalLink,
  CheckCircle2, XCircle, FileWarning, Search, Eye, AlertTriangle,
} from "lucide-react";
import { api, openProtectedFile } from "@/lib/api";

const INS_STATUS_STYLES = {
  pending_review: "bg-amber-500/15 text-amber-200 border-amber-400/40",
  approved:       "bg-teal-500/15 text-teal-200 border-teal-400/40",
  rejected:       "bg-red-500/15 text-red-300 border-red-400/40",
  needs_resubmission: "bg-orange-500/15 text-orange-200 border-orange-400/40",
  expired:        "bg-red-500/15 text-red-300 border-red-400/40",
};

const CHECKLIST_STATUS_STYLES = {
  not_started: "bg-slate-500/15 text-slate-300 border-slate-400/30",
  researching: "bg-amber-500/15 text-amber-200 border-amber-400/40",
  quoted:      "bg-blue-500/15 text-blue-200 border-blue-400/40",
  purchased:   "bg-teal-500/15 text-teal-200 border-teal-400/40",
  n_a:         "bg-slate-500/15 text-slate-400 border-slate-400/30",
};

const INCIDENT_STATUS_STYLES = {
  open:                     "bg-red-500/15 text-red-200 border-red-400/40",
  investigating:            "bg-amber-500/15 text-amber-200 border-amber-400/40",
  documented:               "bg-blue-500/15 text-blue-200 border-blue-400/40",
  resolved:                 "bg-teal-500/15 text-teal-200 border-teal-400/40",
  escalated_to_authorities: "bg-purple-500/15 text-purple-200 border-purple-400/40",
};

export default function AdminRiskCenter() {
  const [insurance, setInsurance] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [checklist, setChecklist] = useState({ items: [], notice: "" });
  const [audit, setAudit] = useState([]);
  const [insFilter, setInsFilter] = useState("pending_review");
  const [incFilter, setIncFilter] = useState("");
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyRow, setVerifyRow] = useState(null);
  const [verifyStatus, setVerifyStatus] = useState("approved");
  const [verifyReason, setVerifyReason] = useState("");
  const [verifyExpiry, setVerifyExpiry] = useState("");
  const [incOpen, setIncOpen] = useState(false);
  const [incRow, setIncRow] = useState(null);
  const [incStatus, setIncStatus] = useState("investigating");
  const [incNotes, setIncNotes] = useState("");
  const [incAction, setIncAction] = useState("");
  const [cleanerLookup, setCleanerLookup] = useState("");
  const [cleanerRisk, setCleanerRisk] = useState(null);

  const load = async () => {
    try {
      const [ins, inc, chk, aud] = await Promise.all([
        api.get(`/admin/insurance${insFilter ? `?status=${insFilter}` : ""}`),
        api.get(`/admin/incidents${incFilter ? `?status=${incFilter}` : ""}`),
        api.get("/admin/business-insurance-checklist"),
        api.get("/admin/audit-log?limit=100"),
      ]);
      setInsurance(ins.data || []);
      setIncidents(inc.data || []);
      setChecklist(chk.data || { items: [], notice: "" });
      setAudit(aud.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load risk center");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [insFilter, incFilter]);

  const openVerify = (row) => {
    setVerifyRow(row);
    setVerifyStatus("approved");
    setVerifyReason("");
    setVerifyExpiry("");
    setVerifyOpen(true);
  };

  const submitVerify = async () => {
    try {
      await api.post("/admin/insurance/verify", {
        insurance_id: verifyRow.id,
        status: verifyStatus,
        reason: verifyReason || null,
        expiration_date_override: verifyExpiry || null,
      });
      toast.success("Insurance record updated");
      setVerifyOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openIncident = (row) => {
    setIncRow(row);
    setIncStatus(row.status === "open" ? "investigating" : row.status);
    setIncNotes("");
    setIncAction(row.action_taken || "");
    setIncOpen(true);
  };

  const submitIncident = async () => {
    try {
      await api.post("/admin/incidents/update", {
        incident_id: incRow.id,
        status: incStatus,
        admin_notes: incNotes,
        action_taken: incAction,
      });
      toast.success("Incident updated");
      setIncOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateChecklist = async (item, patch) => {
    try {
      await api.post("/admin/business-insurance-checklist", {
        key: item.key,
        status: patch.status ?? item.status,
        notes: patch.notes ?? item.notes ?? "",
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const lookupCleaner = async () => {
    if (!cleanerLookup.trim()) return;
    try {
      const { data } = await api.get(`/admin/cleaners/${cleanerLookup.trim()}/risk`);
      setCleanerRisk(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Not found"); }
  };

  const verifyDL = async (cleaner_id, status, reason = null) => {
    if (!cleaner_id) return;
    try {
      await api.post("/admin/cleaners/driver-license/verify", {
        cleaner_id, status, reason,
      });
      toast.success(`Driver's license ${status}`);
      // refresh in-place
      const { data } = await api.get(`/admin/cleaners/${cleaner_id}/risk`);
      setCleanerRisk(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update DL status");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 text-slate-100" data-testid="admin-risk-center">
      <div className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-teal-300">Command Center · Risk & Safety</div>
        <h1 className="font-display font-black text-2xl mt-1">Risk, Insurance & Cleaner Protection</h1>
        <p className="text-sm text-slate-400 mt-1">Verify cleaner insurance, review incidents, and plan CLEANBAZ business coverage.</p>
      </div>

      <Tabs defaultValue="insurance" data-testid="admin-risk-tabs">
        <TabsList className="bg-slate-900/40 border border-white/5">
          <TabsTrigger value="insurance" data-testid="rt-insurance">Insurance queue</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="rt-incidents">Incidents</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="rt-checklist">Business insurance</TabsTrigger>
          <TabsTrigger value="lookup" data-testid="rt-lookup">Cleaner risk profile</TabsTrigger>
          <TabsTrigger value="audit" data-testid="rt-audit">Audit log</TabsTrigger>
        </TabsList>

        {/* Insurance queue */}
        <TabsContent value="insurance" className="mt-6">
          <Card className="bg-slate-900/40 border-white/5">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Status filter</Label>
                <select value={insFilter} onChange={ev => setInsFilter(ev.target.value)} className="h-9 rounded-md bg-slate-950 border border-white/10 text-slate-100 text-sm px-3" data-testid="ins-filter">
                  <option value="">All</option>
                  <option value="pending_review">Pending review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="needs_resubmission">Needs resubmission</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              {insurance.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">No records for this filter.</div>
              ) : (
                <div className="space-y-2" data-testid="ins-queue">
                  {insurance.map(r => (
                    <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-white/5 bg-slate-950/40" data-testid={`ins-queue-row-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-teal-300">{r.cleanbaz_id || "—"}</span>
                          <span className="text-sm font-semibold text-slate-100 truncate">{r.cleaner?.email || "unknown"}</span>
                          <Badge className={`text-[10px] ${INS_STATUS_STYLES[r.status] || ""}`}>{r.status}</Badge>
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          {r.insurance_type} · {r.carrier} · ••••{r.policy_number_last4} · exp {r.expiration_date}
                        </div>
                        {r.document_url && (
                          <button type="button"
                            onClick={async () => {
                              try { await openProtectedFile(r.document_url); }
                              catch (err) { toast.error(err?.response?.status === 403 ? "Not authorized to view this document." : "Unable to load proof document."); }
                            }}
                            className="text-xs text-teal-300 mt-1 inline-flex items-center gap-1 hover:underline bg-transparent p-0"
                            data-testid={`ins-doc-${r.id}`}>
                            <ExternalLink className="w-3 h-3" /> View submitted document
                          </button>
                        )}
                      </div>
                      <Button size="sm" onClick={() => openVerify(r)} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid={`verify-btn-${r.id}`}>
                        Review
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incidents */}
        <TabsContent value="incidents" className="mt-6">
          <Card className="bg-slate-900/40 border-white/5">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Status filter</Label>
                <select value={incFilter} onChange={ev => setIncFilter(ev.target.value)} className="h-9 rounded-md bg-slate-950 border border-white/10 text-slate-100 text-sm px-3" data-testid="inc-filter">
                  <option value="">All</option>
                  <option value="open">Open</option>
                  <option value="investigating">Investigating</option>
                  <option value="documented">Documented</option>
                  <option value="resolved">Resolved</option>
                  <option value="escalated_to_authorities">Escalated</option>
                </select>
              </div>
              {incidents.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">No incidents.</div>
              ) : (
                <div className="space-y-2" data-testid="inc-queue">
                  {incidents.map(i => (
                    <div key={i.id} className="p-3 rounded-lg border border-white/5 bg-slate-950/40" data-testid={`inc-queue-row-${i.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <AlertTriangle className={`w-4 h-4 ${i.severity === "critical" ? "text-red-400" : i.severity === "high" ? "text-orange-400" : "text-amber-300"}`} />
                          <span className="font-semibold text-slate-100">{i.kind.replace(/_/g, " ")}</span>
                          <Badge className="text-[10px] bg-slate-500/15 text-slate-300 border-slate-400/30">{i.severity}</Badge>
                          <Badge className={`text-[10px] ${INCIDENT_STATUS_STYLES[i.status] || ""}`}>{i.status}</Badge>
                          <span className="font-mono text-[11px] text-teal-300">{i.cleanbaz_id}</span>
                          <span className="text-xs text-slate-400 truncate max-w-[280px]">{i.cleaner?.email}</span>
                        </div>
                        <Button size="sm" onClick={() => openIncident(i)} className="bg-slate-800 hover:bg-slate-700 text-slate-100 border border-white/10" data-testid={`inc-update-btn-${i.id}`}>
                          Update
                        </Button>
                      </div>
                      <div className="text-xs text-slate-400 mt-2">Occurred {i.occurred_at} · {i.location || "no location"}</div>
                      <div className="text-sm text-slate-200 mt-2 whitespace-pre-wrap">{i.description}</div>
                      {i.action_taken && <div className="text-xs text-slate-300 mt-2"><b>Action taken:</b> {i.action_taken}</div>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business insurance checklist */}
        <TabsContent value="checklist" className="mt-6">
          <Card className="bg-slate-900/40 border-white/5">
            <CardContent className="p-5">
              <div className="text-xs bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded p-3 mb-4" data-testid="checklist-notice">
                {checklist.notice}
              </div>
              <div className="space-y-2" data-testid="checklist-items">
                {checklist.items?.map(it => (
                  <div key={it.key} className="p-3 rounded-lg border border-white/5 bg-slate-950/40" data-testid={`checklist-${it.key}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="font-semibold text-slate-100">{it.name}</div>
                        <div className="text-xs text-slate-400 mt-1">{it.description}</div>
                      </div>
                      <select value={it.status} onChange={ev => updateChecklist(it, { status: ev.target.value })} className="h-8 rounded-md bg-slate-950 border border-white/10 text-slate-100 text-xs px-2" data-testid={`checklist-status-${it.key}`}>
                        <option value="not_started">Not started</option>
                        <option value="researching">Researching</option>
                        <option value="quoted">Quoted</option>
                        <option value="purchased">Purchased</option>
                        <option value="n_a">Not applicable</option>
                      </select>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Badge className={`text-[10px] ${CHECKLIST_STATUS_STYLES[it.status] || ""}`}>{it.status}</Badge>
                      <Input placeholder="Agent notes, carrier, quote details…" defaultValue={it.notes || ""} onBlur={ev => { if ((ev.target.value || "") !== (it.notes || "")) updateChecklist(it, { notes: ev.target.value }); }} className="flex-1 bg-slate-950 border-white/10 text-slate-100 h-8 text-xs" data-testid={`checklist-notes-${it.key}`} />
                    </div>
                    {it.updated_by && <div className="text-[10px] text-slate-500 mt-1">Last updated by {it.updated_by} · {it.updated_at}</div>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cleaner risk profile lookup */}
        <TabsContent value="lookup" className="mt-6">
          <Card className="bg-slate-900/40 border-white/5">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Input placeholder="Cleaner user_id" value={cleanerLookup} onChange={ev => setCleanerLookup(ev.target.value)} className="bg-slate-950 border-white/10 text-slate-100 h-9" data-testid="risk-lookup-input" />
                <Button onClick={lookupCleaner} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="risk-lookup-btn"><Search className="w-4 h-4 mr-1" />Look up</Button>
              </div>
              {cleanerRisk && (
                <div className="space-y-4" data-testid="risk-profile-detail">
                  <div className="p-3 rounded-lg border border-white/5 bg-slate-950/40">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-teal-300">{cleanerRisk.profile.cleanbaz_id}</span>
                      <span className="font-semibold">{cleanerRisk.profile.legal_first_name} {cleanerRisk.profile.legal_last_name}</span>
                      <span className="text-xs text-slate-400">{cleanerRisk.user?.email}</span>
                      <Badge className="text-[10px] bg-slate-500/15 text-slate-200">{cleanerRisk.profile.verification_status}</Badge>
                    </div>
                    <div className="text-xs text-slate-400 mt-2">
                      Eligible for transport jobs: <b className={cleanerRisk.eligibility.eligible_for_transport_jobs ? "text-teal-300" : "text-amber-300"}>{String(cleanerRisk.eligibility.eligible_for_transport_jobs)}</b>
                      {" · "}Agreement: <b>{cleanerRisk.eligibility.agreement_ok ? "accepted" : "not accepted"}</b>
                      {" · "}Auto insurance: <b>{cleanerRisk.eligibility.auto_insurance_active ? cleanerRisk.eligibility.auto_insurance_expiration : "n/a"}</b>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Insurance ({cleanerRisk.insurance.length})</div>
                    {cleanerRisk.insurance.map(r => (
                      <div key={r.id} className="p-2 rounded border border-white/5 bg-slate-950/40 mb-1 text-xs flex items-center gap-2">
                        <Badge className={`text-[10px] ${INS_STATUS_STYLES[r.status] || ""}`}>{r.status}</Badge>
                        <span>{r.insurance_type} · {r.carrier} · ••••{r.policy_number_last4} · exp {r.expiration_date}</span>
                        {r.document_url && <button type="button" className="text-teal-300 hover:underline ml-auto bg-transparent p-0" onClick={async () => { try { await openProtectedFile(r.document_url); } catch (err) { toast.error(err?.response?.status === 403 ? "Not authorized to view this document." : "Unable to load proof document."); } }}><ExternalLink className="w-3 h-3 inline" /> Doc</button>}
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Incidents ({cleanerRisk.incidents.length})</div>
                    {cleanerRisk.incidents.map(i => (
                      <div key={i.id} className="p-2 rounded border border-white/5 bg-slate-950/40 mb-1 text-xs">
                        <Badge className={`text-[10px] ${INCIDENT_STATUS_STYLES[i.status] || ""}`}>{i.status}</Badge>
                        <span className="ml-2">{i.kind.replace(/_/g, " ")} · {i.severity} · {i.occurred_at}</span>
                        <div className="text-slate-300 mt-1">{i.description}</div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Vehicle & Driver's License</div>
                    {(() => {
                      const v = cleanerRisk.vehicle || {};
                      const dlBadge =
                        v.dl_status === "verified" ? "bg-teal-500/15 text-teal-200 border-teal-400/40" :
                        v.dl_status === "rejected" ? "bg-red-500/15 text-red-300 border-red-400/40" :
                        v.dl_status === "pending"  ? "bg-amber-500/15 text-amber-200 border-amber-400/40" :
                        "bg-slate-500/15 text-slate-300 border-slate-400/30";
                      return (
                        <div className="p-3 rounded border border-white/5 bg-slate-950/40 text-xs space-y-2" data-testid="admin-vehicle-dl">
                          <div className="grid grid-cols-2 gap-2">
                            <div><span className="text-slate-400">Transport:</span> <b className="text-slate-200">{v.transportation_type || "—"}</b></div>
                            <div><span className="text-slate-400">Vehicle type:</span> <b className="text-slate-200">{v.vehicle_type || "—"}</b></div>
                            <div><span className="text-slate-400">Year/Make/Model:</span> <b className="text-slate-200">{[v.year, v.make, v.model].filter(Boolean).join(" ") || "—"}</b></div>
                            <div><span className="text-slate-400">Color:</span> <b className="text-slate-200">{v.color || "—"}</b></div>
                            <div><span className="text-slate-400">Plate:</span> <b className="text-slate-200">{v.plate_state ? `${v.plate_state} · ` : ""}{v.license_plate || "—"}</b></div>
                            <div><span className="text-slate-400">VIN (last):</span> <b className="text-slate-200">{v.vin_last6 || "—"}</b></div>
                          </div>
                          <div className="pt-2 border-t border-white/5 flex items-center gap-2 flex-wrap">
                            <Badge className={`text-[10px] ${dlBadge}`} data-testid="admin-dl-status">DL {v.dl_status || "not submitted"}</Badge>
                            <span className="text-slate-300">State <b>{v.dl_state || "—"}</b> · Expires <b>{v.dl_expiration_date || "—"}</b> · Last4 <b>{v.dl_last4 || "—"}</b></span>
                            <div className="ml-auto flex items-center gap-2">
                              {v.dl_front_url && (
                                <button type="button"
                                  onClick={async () => {
                                    try { await openProtectedFile(v.dl_front_url); }
                                    catch (err) { toast.error(err?.response?.status === 403 ? "Not authorized to view this document." : "Unable to load driver's license photo."); }
                                  }}
                                  className="text-teal-300 hover:underline bg-transparent p-0"
                                  data-testid="admin-dl-front-view">
                                  <ExternalLink className="w-3 h-3 inline" /> Front
                                </button>
                              )}
                              {v.dl_back_url && (
                                <button type="button"
                                  onClick={async () => {
                                    try { await openProtectedFile(v.dl_back_url); }
                                    catch (err) { toast.error(err?.response?.status === 403 ? "Not authorized to view this document." : "Unable to load driver's license photo."); }
                                  }}
                                  className="text-teal-300 hover:underline bg-transparent p-0"
                                  data-testid="admin-dl-back-view">
                                  <ExternalLink className="w-3 h-3 inline" /> Back
                                </button>
                              )}
                            </div>
                          </div>
                          {(v.dl_state || v.dl_expiration_date || v.dl_last4 || v.dl_front_url) && (
                            <div className="flex items-center gap-2 pt-2 border-t border-white/5" data-testid="admin-dl-verify-row">
                              <Button
                                data-testid="admin-dl-approve"
                                onClick={() => verifyDL(cleanerRisk.user?.id, "verified")}
                                className="h-7 px-2 text-[11px] bg-teal-600 hover:bg-teal-500 text-white"
                              ><CheckCircle2 className="w-3 h-3 mr-1" />Approve DL</Button>
                              <Button
                                data-testid="admin-dl-reject"
                                onClick={() => {
                                  const reason = window.prompt("Reject reason (visible to the cleaner):", "Photo unclear — please re-upload front and back of your license.");
                                  if (reason == null) return;
                                  verifyDL(cleanerRisk.user?.id, "rejected", reason.trim() || null);
                                }}
                                className="h-7 px-2 text-[11px] bg-red-600 hover:bg-red-500 text-white"
                              ><XCircle className="w-3 h-3 mr-1" />Reject DL</Button>
                              <Button
                                data-testid="admin-dl-pending"
                                variant="outline"
                                onClick={() => verifyDL(cleanerRisk.user?.id, "pending")}
                                className="h-7 px-2 text-[11px]"
                              >Mark pending</Button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit log */}
        <TabsContent value="audit" className="mt-6">
          <Card className="bg-slate-900/40 border-white/5">
            <CardContent className="p-5">
              <div className="text-xs text-slate-400 mb-3">Last 100 sensitive admin actions.</div>
              <div className="space-y-1" data-testid="audit-log">
                {audit.map(a => (
                  <div key={a.id} className="text-xs p-2 rounded border border-white/5 bg-slate-950/40 flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-slate-400">{a.at}</span>
                    <Badge className="text-[10px] bg-slate-500/15 text-slate-200">{a.action}</Badge>
                    <span className="text-slate-300 truncate">{a.actor_email}</span>
                    <span className="text-slate-400">→ {JSON.stringify(a.target)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Insurance verify dialog */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent data-testid="ins-verify-dialog">
          <DialogHeader><DialogTitle>Verify insurance</DialogTitle></DialogHeader>
          {verifyRow && (
            <div className="space-y-3 text-sm">
              <div className="text-slate-500">{verifyRow.cleaner?.email} · {verifyRow.insurance_type} · {verifyRow.carrier}</div>
              {verifyRow.document_url && (
                <button type="button"
                  onClick={async () => {
                    try { await openProtectedFile(verifyRow.document_url); }
                    catch (err) { toast.error(err?.response?.status === 403 ? "Not authorized to view this document." : "Unable to load proof document."); }
                  }}
                  className="inline-flex items-center gap-1 text-teal-600 hover:underline text-sm bg-transparent p-0"
                  data-testid="verify-ins-dialog-doc-legacy">
                  <Eye className="w-4 h-4" /> View submitted document
                </button>
              )}
              <div>
                <Label>Decision</Label>
                <select value={verifyStatus} onChange={ev => setVerifyStatus(ev.target.value)} className="mt-1 w-full h-10 border rounded-md px-3 text-sm" data-testid="verify-status">
                  <option value="approved">Approve</option>
                  <option value="rejected">Reject</option>
                  <option value="needs_resubmission">Needs resubmission</option>
                </select>
              </div>
              <div><Label>Reason / notes {verifyStatus !== "approved" ? "*" : "(optional)"}</Label><Textarea value={verifyReason} onChange={ev => setVerifyReason(ev.target.value)} rows={3} data-testid="verify-reason" /></div>
              <div><Label>Override expiration (optional)</Label><Input type="date" value={verifyExpiry} onChange={ev => setVerifyExpiry(ev.target.value)} data-testid="verify-expiry" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyOpen(false)}>Cancel</Button>
            <Button onClick={submitVerify} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="verify-submit">Save decision</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incident update dialog */}
      <Dialog open={incOpen} onOpenChange={setIncOpen}>
        <DialogContent data-testid="inc-update-dialog">
          <DialogHeader><DialogTitle>Update incident</DialogTitle></DialogHeader>
          {incRow && (
            <div className="space-y-3 text-sm">
              <div className="text-slate-500">{incRow.kind.replace(/_/g, " ")} · {incRow.severity} · {incRow.cleaner?.email}</div>
              <div>
                <Label>Status</Label>
                <select value={incStatus} onChange={ev => setIncStatus(ev.target.value)} className="mt-1 w-full h-10 border rounded-md px-3 text-sm" data-testid="inc-status">
                  <option value="open">Open</option>
                  <option value="investigating">Investigating</option>
                  <option value="documented">Documented</option>
                  <option value="resolved">Resolved</option>
                  <option value="escalated_to_authorities">Escalated to authorities</option>
                </select>
              </div>
              <div><Label>Admin note (audit-logged)</Label><Textarea value={incNotes} onChange={ev => setIncNotes(ev.target.value)} rows={3} data-testid="inc-note" /></div>
              <div><Label>Action taken</Label><Textarea value={incAction} onChange={ev => setIncAction(ev.target.value)} rows={2} data-testid="inc-action" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncOpen(false)}>Cancel</Button>
            <Button onClick={submitIncident} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="inc-update-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
